import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IAgenda,
  IAgendamento,
  ICapturaLocalizacao,
  IClienteDependenteEntity,
  IFeriado,
  IPessoaBasico,
  StatusAgendamentoEnum,
} from '@nomoh-monorepo/shared-types';
import { LuxonNomoh } from '@nomoh-monorepo/shared-util-luxon-nomoh';
import { DateTime } from 'luxon';
import { firstValueFrom, lastValueFrom } from 'rxjs';

import { EstabelecimentoDataService } from '../feature-estabelecimento/estabelecimento.dataservice';
import { WhatsappConfigDataService } from '../feature-whatsapp-config/whatsapp-config.dataservice';
import { AgendaDataService } from './agenda.dataservice';

import {
  formatarDataHoraParaWhatsapp
} from '@nomoh-monorepo/shared-util-tools';
import { ProfissionalDataService } from '../feature-profissional/profissional.dataservice';
import { TipoServicoDataService } from '../feature-tipo-servico/tipo-servico.dataservice';

type FeriadoComStatus = IFeriado & { status?: number };

export type InserirAgendamentoPayload = {
  agendaId: string;
  dataHora: Date;
  servicoId: string;
  profissionalId: string;
  clienteId: string;
  telefoneCliente: string;
  nomeCliente: string;
  status?: StatusAgendamentoEnum;
  observacao?: string;
  telefoneEstabelecimento?: string;
  verificarDisponibilidade?: boolean;
  isEncaixe?: boolean;
  isDomicilio?: boolean;
  endereco?: string | null;
};

/**
 * Service HTTP para comunicação com a API de Agenda
 * Segue o padrão do monorepo (ver: libs/data-access/nomoh-administrador/feature-planos)
 *
 * ✅ Usa interfaces de @nomoh-monorepo/shared-types
 * ✅ Atualiza o DataService após operações
 */
@Injectable({ providedIn: 'root' })
export class AgendaService {
  private readonly http = inject(HttpClient);
  private readonly notificacaoService = inject(NotificacaoService);
  private readonly dataService = inject(AgendaDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private whatsappConfigDataService = inject(WhatsappConfigDataService);
  private readonly tipoServicoDataService = inject(TipoServicoDataService);
  private readonly profissionalDataService = inject(ProfissionalDataService);

  private environment = inject(EnvironmentService);
  private readonly agendaApiUrl = `${this.environment.getApiUrl(
    'bardo'
  )}/agenda`;
  private readonly pessoaApiUrl = `${this.environment.getApiUrl(
    'nomoh'
  )}/pessoa-cliente`;

  private readonly whatsappUrl = `${this.environment.getApiUrl(
    'zap'
  )}/whatsapp`;
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  async buscarAgenda(): Promise<IAgenda> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const agenda = await firstValueFrom(
        this.http.get<IAgenda>(`${this.agendaApiUrl}/find`)
      );
      this.dataService.setAgendaInDb(agenda);
      this.dataService.setAgenda(agenda);
      return agenda;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar agenda';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async cancelarAgendamentoModalInformacao(
    agendamento: IAgendamento | null,
    observacao: string,
    localizacao?: ICapturaLocalizacao
  ): Promise<boolean> {
    const observacaoTratada = observacao?.trim();
    if (!observacaoTratada) {
      this.notificacaoService.notificacaoWarn(
        'Informe o motivo do cancelamento para continuar.'
      );
      return false;
    }

    if (!agendamento) {
      return false;
    }

    return await this.atualizarStatusAgendamento(
      agendamento,
      () => this.cancelarAgendamento(agendamento, observacaoTratada, localizacao)
    ).finally(() => {
      this.enviarMensagemcancelamentoAgendamento(
        agendamento,
        observacaoTratada
      );
    });
  }

  async aprovarAgendamentoModalInformacao(
    agendamento: IAgendamento | null,
    localizacao?: ICapturaLocalizacao
  ): Promise<boolean> {
    if (!agendamento) {
      return false;
    }

    const agendamentoId = agendamento.id;

    return await this.atualizarStatusAgendamento(
      agendamento,
      () => this.aprovarAgendamento(agendamentoId, localizacao)
    ).finally(() => {
      this.enviarMensagemAprovacaoAgendamento(agendamento);
    });
  }

  async realizarAgendamentoModalInformacao(
    agendamento: IAgendamento | null,
    localizacao?: ICapturaLocalizacao
  ): Promise<boolean> {
    if (!agendamento) {
      return false;
    }

    const agendamentoId = agendamento.id;

    return await this.atualizarStatusAgendamento(
      agendamento,
      () => this.realizarAgendamento(agendamentoId, localizacao)
    ).finally(() => {
      this.enviarMensagemRealizacaoAgendamento(agendamento);
    });
  }

  private async atualizarStatusAgendamento(
    agendamento: IAgendamento | null,
    requisicao: () => Promise<boolean>
  ): Promise<boolean> {
    if (!agendamento) {
      return false;
    }

    this.dataService.setIsLoadingModalInformacao(true);

    try {
      const resultado = await requisicao();

      if (!resultado) {
        throw new Error('Operação não concluída.');
      }

      await this.atualizarAgendaAposOperacao();

      return true;
    } catch (error) {
      console.error('AgendaService atualizarStatusAgendamento', error);
      return false;
    } finally {
      this.dataService.setIsLoadingModalInformacao(false);
    }
  }

  private async cancelarAgendamento(
    agendamento: IAgendamento,
    observacao: string,
    localizacao?: ICapturaLocalizacao
  ): Promise<boolean> {
    return await firstValueFrom(
      this.http.put<boolean>(`${this.agendaApiUrl}/cancelarAgendamento`, {
        id: agendamento.id,
        observacao: observacao,
        ...localizacao,
      })
    );
  }

  private async aprovarAgendamento(
    id: string,
    localizacao?: ICapturaLocalizacao
  ): Promise<boolean> {
    return await firstValueFrom(
      this.http.put<boolean>(`${this.agendaApiUrl}/aprovarAgendamento`, {
        id,
        ...localizacao,
      })
    );
  }

  private isWhatsappConnected(): boolean {
    return this.whatsappConfigDataService.$whatsappConectado();
  }

  private formatarDataHoraAgendamento(
    dataHora: Date | string | null | undefined
  ): { data: string; hora: string } {
    return formatarDataHoraParaWhatsapp(
      dataHora,
      this.getTimeZoneEstabelecimento()
    );
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;

    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }

  private enviarMensagemcancelamentoAgendamento(
    agendamento: IAgendamento,
    observacao: string
  ) {
    if (!this.isWhatsappConnected()) {
      return;
    }

    const sessionName = this.buildSessionName(
      this.estabelecimentoDataService.$estabelecimentoAtual()?.nome,
      this.estabelecimentoDataService.$estabelecimentoAtual()
        ?.fluxosAtendimento?.[0]?.id || ''
    );


    const { data, hora } = this.formatarDataHoraAgendamento(
      agendamento?.dataHora
    );
    const mensagemWhatsapp = `Agendamento Cancelado! ❌

    Prezado, *${agendamento?.nomeCliente}*,

    Lamentamos informar que seu agendamento precisou ser cancelado.

    📅 Data: *${data}*
    ⏰ Horário: *${hora}*
    ✂️ Profissional: *${agendamento?.profissional!.nome}*
    💇‍♀️ Serviço: *${agendamento?.servico!.nome}*
    \n\nPedimos sinceras desculpas pelo inconveniente. Caso deseje, podemos reagendar para outra data ou horário disponível.\n\nMotivo: *${observacao}*\n\nAgradecemos sua compreensão e estamos à disposição para mais informações.`;

    const url = `${this.whatsappUrl}/${sessionName}/send/text`;

    firstValueFrom(
      this.http.post(url, {
        instanceName: sessionName,
        to: agendamento?.telefoneCliente,
        text: mensagemWhatsapp,
      })
    );
  }

  private enviarMensagemAprovacaoAgendamento(agendamento: IAgendamento) {
    if (!this.isWhatsappConnected()) {
      return;
    }

    const sessionName = this.buildSessionName(
      this.estabelecimentoDataService.$estabelecimentoAtual()?.nome,
      this.estabelecimentoDataService.$estabelecimentoAtual()
        ?.fluxosAtendimento?.[0]?.id || ''
    );
    const instagram = '';

    const { data, hora } = this.formatarDataHoraAgendamento(
      agendamento?.dataHora
    );

    // if(agendamento?.instagram){
    //   instagram = `\\n\n*📲 Nos siga nas redes sociais:* ${this.estabelecimentoDataService.$estabelecimentoAtual()?.}*`;
    // }


    //let instancia = this.estabelecimentoDataService.estabelecimento.fluxosAtendimento[0]!.whatsappAuth?.evoltionInstance;
    const mensagemWhatsapp = `Agendamento Confirmado! ✅\n \nMuito obrigado, *${agendamento?.nomeCliente}*! Seu agendamento foi realizado com sucesso.\n\n📅 Data: *${data}*\n⏰ Horário: *${hora}*\n✂️ Profissional: *${agendamento?.profissional?.nome}*\n💇‍♀️ Serviço: *${agendamento?.servico?.nome}*\n\nAguardamos você com grande satisfação! 😊\n\nAgradecemos o seu contato e estamos sempre à disposição.${instagram}`;
    const latitude =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco
        .latitude;
    const longitude =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco
        .longitude;
    const endereco = `${this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco
      .logradouro
      }, ${this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco.numero
      } ${this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco.bairro
      } ${this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco.cidade
      } ${this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco.estado
      } ${this.estabelecimentoDataService.$estabelecimentoAtual()?.endereco
        .codPostal
      }`;
    const nomeEstabelecimento =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.nome;

    const url = `${this.whatsappUrl}/${sessionName}/send/text`;

    firstValueFrom(
      this.http.post(url, {
        instanceName: sessionName,
        to: agendamento?.telefoneCliente,
        text: mensagemWhatsapp,
      })
    ).then(() => {
      if (latitude && longitude) {
        const url = `${this.whatsappUrl}/${sessionName}/send/location`;
        firstValueFrom(
          this.http.post(url, {
            to: agendamento?.telefoneCliente,
            latitude: +latitude,
            longitude: +longitude,
            name: nomeEstabelecimento,
            address: endereco,
          })
        );
      }
    });
  }

  private enviarMensagemRealizacaoAgendamento(agendamento: IAgendamento) {
    if (!this.isWhatsappConnected()) {
      return;
    }


    const sessionName = this.buildSessionName(
      this.estabelecimentoDataService.$estabelecimentoAtual()?.nome,
      this.estabelecimentoDataService.$estabelecimentoAtual()
        ?.fluxosAtendimento?.[0]?.id || ''
    );

    const mensagemWhatsapp = `${agendamento?.nomeCliente}, muito obrigado por escolher nossos serviços!\nFicamos muito felizes em atender você. Conte com a gente sempre que precisar! 😊`;

    const url = `${this.whatsappUrl}/${sessionName}/send/text`;

    firstValueFrom(
      this.http.post(url, {
        instanceName: sessionName,
        to: agendamento?.telefoneCliente,
        text: mensagemWhatsapp,
      })
    );
  }

  private enviarMensagemReagendamentoAgendamento(agendamento: IAgendamento) {
    if (!this.isWhatsappConnected()) {
      return;
    }


    const sessionName = this.buildSessionName(
      this.estabelecimentoDataService.$estabelecimentoAtual()?.nome,
      this.estabelecimentoDataService.$estabelecimentoAtual()
        ?.fluxosAtendimento?.[0]?.id || ''
    );

    const servico = this.tipoServicoDataService
      .$tiposServico()
      .find((s) => s.servicos?.find((s) => s.id === agendamento?.servicoId))
      ?.servicos?.find((s) => s.id === agendamento?.servicoId);

    const profissional = this.profissionalDataService
      .$profissionais()
      .find((p) => p.id === agendamento?.profissionalId);

    const { data, hora } = this.formatarDataHoraAgendamento(
      agendamento?.dataHora
    );
    const mensagemWhatsapp = `${agendamento?.nomeCliente}, você foi reagendado com sucesso! \n\n Para o dia: *${data}* às *${hora}* com o profissional *${profissional?.nome}* para o serviço *${servico?.nome}*.\n\n Agradecemos, estamos à disposição para mais informações.`;

    const url = `${this.whatsappUrl}/${sessionName}/send/text`;

    firstValueFrom(
      this.http.post(url, {
        instanceName: sessionName,
        to: agendamento?.telefoneCliente,
        text: mensagemWhatsapp,
      })
    );
  }

  private async realizarAgendamento(
    id: string,
    localizacao?: ICapturaLocalizacao
  ): Promise<boolean> {
    return await firstValueFrom(
      this.http.put<boolean>(`${this.agendaApiUrl}/realizarAgendamento`, {
        id,
        ...localizacao,
      })
    );
  }

  private async atualizarAgendaAposOperacao(): Promise<void> {
    const agenda = await firstValueFrom(
      this.http.get<IAgenda>(`${this.agendaApiUrl}/find`)
    );
    this.dataService.setAgendaInDb(agenda);
    this.dataService.setAgenda(agenda);
  }

  async buscarDependentesDe(clienteId: string): Promise<IClienteDependenteEntity[]> {
    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.agendaApiUrl}/cliente/dependentes`, {
          params: { clienteId },
        })
      );
      return Array.isArray(res) ? res : (res?.data || []);
    } catch (error) {
      console.error('AgendaService buscarDependentesDe', error);
      return [];
    }
  }

  async buscarResponsaveisDe(clienteId: string): Promise<IClienteDependenteEntity[]> {
    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.agendaApiUrl}/cliente/responsaveis`, {
          params: { clienteId },
        })
      );
      return Array.isArray(res) ? res : (res?.data || []);
    } catch (error) {
      console.error('AgendaService buscarResponsaveisDe', error);
      return [];
    }
  }

  async buscarClientePorTelefone(
    telefone: string
  ): Promise<IPessoaBasico | null> {
    const sanitizedTelefone = telefone?.trim();
    if (!sanitizedTelefone) {
      return null;
    }
    const agendaId = this.dataService.$agenda()?.id;
    if (!agendaId) return null;

    try {
      const resposta = await firstValueFrom(
        this.http.get<IPessoaBasico | IPessoaBasico[]>(
          `${this.agendaApiUrl}/cliente/telefone`,
          {
            params: { telefone: sanitizedTelefone, agendaId },
          }
        )
      );

      if (Array.isArray(resposta)) {
        return null;
      }

      return resposta;
    } catch (error) {
      console.error('AgendaService buscarClientePorTelefone', error);
      return null;
    }
  }

  async cadastrarClienteRapido(payload: {
    nome: string;
    telefone?: string;
    responsavelId?: string;
    dataNascimento?: Date | string;
  }): Promise<IPessoaBasico | null> {
    const nome = payload.nome?.trim();
    const telefone = payload.telefone ? payload.telefone.trim().replace(/\D/g, '') : undefined;

    if (!nome) {
      return null;
    }

    const agendaId = this.dataService.$agenda()?.id;
    if (!agendaId) return null;

    try {
      const resposta = await firstValueFrom(
        this.http.post<IPessoaBasico>(`${this.agendaApiUrl}/cliente/rapido`, {
          nome,
          telefone,
          dataNascimento: payload.dataNascimento,
          agendaId,
          responsavelId: payload.responsavelId,
        })
      );

      return resposta;
    } catch (error) {
      console.error('AgendaService cadastrarClienteRapido', error);
      return null;
    }
  }

  async inserirAgendamento(
    payload: InserirAgendamentoPayload
  ): Promise<string> {
    const body = {
      ...payload,
      status: payload.status ?? StatusAgendamentoEnum.PENDENTE,
    };

    try {
      const id = await firstValueFrom(
        this.http.put<string>(`${this.agendaApiUrl}/inserirAgendamento`, body)
      );

      if (!id) {
        throw new Error('Resposta inválida da API ao inserir agendamento.');
      }

      const novoAgendamento: IAgendamento = {
        id,
        agendaId: body.agendaId,
        profissionalId: body.profissionalId,
        servicoId: body.servicoId,
        clienteId: body.clienteId,
        dataHora: body.dataHora,
        status: body.status ?? StatusAgendamentoEnum.PENDENTE,
        nomeCliente: body.nomeCliente,
        telefoneCliente: body.telefoneCliente,
        observacao: body.observacao,
        telefoneEstabelecimento: body.telefoneEstabelecimento,
        duracao: '00:00:00',
        isDomicilio: body.isDomicilio,
        endereco: body.endereco,
      };
      this.dataService.addAgendamento(novoAgendamento);

      return id;
    } catch (error) {
      console.error('AgendaService inserirAgendamento', error);
      throw error;
    }
  }

  /**
   * Cria um encaixe (agendamento extraordinário) via rota dedicada
   * `PUT /agenda/inserirEncaixe`, que ignora a validação de
   * disponibilidade/conflito de horário no backend.
   */
  async inserirEncaixe(payload: InserirAgendamentoPayload): Promise<string> {
    const body = {
      ...payload,
      status: payload.status ?? StatusAgendamentoEnum.PENDENTE,
    };

    try {
      const id = await firstValueFrom(
        this.http.put<string>(`${this.agendaApiUrl}/inserirEncaixe`, body)
      );

      if (!id) {
        throw new Error('Resposta inválida da API ao inserir encaixe.');
      }

      const novoEncaixe: IAgendamento = {
        id,
        agendaId: body.agendaId,
        profissionalId: body.profissionalId,
        servicoId: body.servicoId,
        clienteId: body.clienteId,
        dataHora: body.dataHora,
        status: body.status ?? StatusAgendamentoEnum.PENDENTE,
        nomeCliente: body.nomeCliente,
        telefoneCliente: body.telefoneCliente,
        observacao: body.observacao,
        telefoneEstabelecimento: body.telefoneEstabelecimento,
        duracao: '00:00:00',
        isEncaixe: true,
        isDomicilio: body.isDomicilio,
        endereco: body.endereco,
      };
      this.dataService.addAgendamento(novoEncaixe);

      return id;
    } catch (error) {
      console.error('AgendaService inserirEncaixe', error);
      throw error;
    }
  }

  async atualizarAgendamento(
    payload: InserirAgendamentoPayload & { id: string }
  ): Promise<boolean> {
    const body = {
      ...payload,
      status: payload.status ?? StatusAgendamentoEnum.PENDENTE,
    };

    try {
      const resultado = await firstValueFrom(
        this.http.put<boolean>(
          `${this.agendaApiUrl}/atualizarAgendamento`,
          body
        )
      ).finally(() => {
        this.enviarMensagemReagendamentoAgendamento(
          payload as unknown as IAgendamento
        );
      });

      if (resultado === undefined || resultado === null) {
        throw new Error('Resposta inválida da API ao atualizar agendamento.');
      }

      await this.atualizarAgendaAposOperacao();

      return resultado;
    } catch (error) {
      console.error('AgendaService atualizarAgendamento', error);
      throw error;
    }
  }

  async buscarFeriados(): Promise<void> {
    const anoAtual = LuxonNomoh.hoje().year;
    const anos = [anoAtual, anoAtual + 1];

    try {
      const respostas = await Promise.all(
        anos.map((ano) => this.buscarFeriadosPorAno(ano))
      );

      const feriadosUnicos = new Map<string, FeriadoComStatus>();
      respostas
        .flat()
        .forEach((feriado) => feriadosUnicos.set(feriado.date, { ...feriado }));

      const feriados = Array.from(feriadosUnicos.values());

      this.dataService.setFeriados(feriados);
      this.dataService.setFeriadosInDb(feriados);
    } catch (error) {
      console.error('AgendaFiltroService.buscarFeriados', error);
      throw error;
    }
  }

  private async buscarFeriadosPorAno(ano: number): Promise<FeriadoComStatus[]> {
    const url = `https://brasilapi.com.br/api/feriados/v1/${ano}`;
    const response = await lastValueFrom(
      this.http.get<FeriadoComStatus[]>(url, { withCredentials: false })
    );

    return response.map((feriado) => ({
      date: feriado.date,
      name: feriado.name,
      type: feriado.type,
      status: feriado.status ?? 0,
    }));
  }

  //TODO: Remover após o refactor do whatsapp
  private buildSessionName(
    nomeEstabelecimento: string | undefined,
    fluxoId: string | null
  ): string {
    const nameSlug =
      nomeEstabelecimento
        ?.normalize('NFD')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() ?? 'estabelecimento';

    return `${nameSlug}-${fluxoId}`;
  }
}
